import _extends from '@babel/runtime/helpers/extends';
import React from 'react';
import PropTypes from 'prop-types';
import { p as props } from './componentEl-fcbc301e.js';
import { a as addClassNames, c as classNamePropTypes } from './classNames-1ed461aa.js';
import { a as addSpacingClasses, m as marginPropTypes, p as paddingPropTypes } from './spacing-0f39ba4a.js';
import IconCaretLeft from './IconCaretLeft.js';
import IconCaretRight from './IconCaretRight.js';
import { getContentClasses } from './useSidebarState.js';
import { g as getZIndex, z as zIndexProps } from './zIndex-60238fbe.js';

function SidebarSection({
  componentEl: Component,
  children,
  className,
  margin,
  padding,
  scrollable,
  ...otherProps
}) {
  let cssClasses = ['st_sidebar__section'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  if (scrollable) {
    cssClasses.push('st_sidebar__section--scrollable');
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SidebarSection.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for section, if any */
  margin: marginPropTypes,
  /** margin for section, if any */
  padding: paddingPropTypes,
  /** whether this section should show scrollbars if too much content */
  scrollable: PropTypes.bool
};
SidebarSection.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: null,
  scrollable: true
};
SidebarSection.displayName = 'Sidebar.Section';

function SidebarFooter({
  componentEl: Component,
  children,
  className,
  margin,
  padding,
  border,
  ...otherProps
}) {
  let cssClasses = ['st_sidebar__section', 'st_sidebar__section--sticky-bottom'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  if (border) cssClasses.push('st_sidebar__section--border');
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SidebarFooter.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin, if any */
  margin: marginPropTypes,
  /** margin, if any */
  padding: paddingPropTypes,
  /** whether footer should have a border on the top */
  border: PropTypes.bool
};
SidebarFooter.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  border: true
};
SidebarFooter.displayName = 'Sidebar.Footer';

function SidebarHeader({
  componentEl: Component,
  children,
  className,
  margin,
  padding,
  border,
  ...otherProps
}) {
  let cssClasses = ['st_sidebar__section', 'st_sidebar__section--sticky-top'];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  if (border) {
    cssClasses.push('st_sidebar__section--border');
  }
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SidebarHeader.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** padding for component, if any */
  padding: paddingPropTypes,
  /** whether header should have a border on the bottom */
  border: PropTypes.bool
};
SidebarHeader.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  border: true
};
SidebarHeader.displayName = 'Sidebar.Header';

function SidebarToggle({
  componentEl: Component,
  className,
  margin,
  padding,
  status,
  onCollapse,
  onOpen,
  position,
  ...otherProps
}) {
  let cssClasses = ['st_sidebar__toggle', `st_sidebar__toggle--${position}`, `st_sidebar__toggle--${status}`];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  const getIcon = sidebarStatus => {
    switch (sidebarStatus) {
      case 'open':
        if (position === 'left') return IconCaretLeft;
        return IconCaretRight;
      default:
        if (position === 'left') return IconCaretRight;
        return IconCaretLeft;
    }
  };
  const ToggleIcon = getIcon(status);
  const handleClick = e => {
    e.preventDefault();
    if (status === 'open' || status === 'opening') {
      onCollapse();
    } else {
      onOpen();
    }
  };
  const label = status === 'open' ? 'close sidebar' : 'open sidebar';
  return /*#__PURE__*/React.createElement("div", {
    className: cssClasses.join(' ')
  }, /*#__PURE__*/React.createElement("button", _extends({
    className: "st_sidebar__togglebtn",
    type: "button",
    "aria-label": label,
    onClick: handleClick
  }, otherProps), /*#__PURE__*/React.createElement(ToggleIcon, {
    size: "mlg"
  })));
}
SidebarToggle.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for component, if any */
  margin: marginPropTypes,
  /** padding for component, if any. Overrides default */
  padding: paddingPropTypes,
  /** current status */
  status: PropTypes.oneOf(['open', 'opening', 'collapsed', 'collapsing', 'closed', 'closing']),
  /** event handler when collapse button is clicked */
  onCollapse: PropTypes.func,
  /** event handler when open button is clicked */
  onOpen: PropTypes.func,
  /** position of sidebar */
  position: PropTypes.oneOf(['left', 'right'])
};
SidebarToggle.defaultProps = {
  componentEl: 'button',
  className: null,
  margin: null,
  padding: {
    all: 'xs'
  },
  status: 'open',
  onCollapse: () => null,
  onOpen: () => null,
  position: 'left'
};
SidebarToggle.displayName = 'Sidebar.Toggle';

function SidebarPage({
  componentEl: Component,
  children,
  className,
  margin,
  padding,
  status,
  type,
  position,
  ...otherProps
}) {
  let cssClasses = getContentClasses({
    type,
    status,
    position
  });
  cssClasses = addClassNames(className, cssClasses);
  cssClasses = addSpacingClasses({
    margin,
    padding,
    cssClasses
  });
  return /*#__PURE__*/React.createElement(Component, _extends({
    className: cssClasses.join(' ')
  }, otherProps), children);
}
SidebarPage.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** margin for section, if any */
  margin: marginPropTypes,
  /** margin for section, if any */
  padding: paddingPropTypes,
  /** whether sidebar is open (if it's being controlled) */
  status: PropTypes.oneOf(['open', 'closed', 'collapsed', 'opening', 'closing', 'collapsing']),
  /** type of sidebar */
  type: PropTypes.oneOf(['permanent', 'slide', 'push', 'overlay']),
  /** the position of the sidebar */
  position: PropTypes.oneOf(['left', 'right'])
};
SidebarPage.defaultProps = {
  componentEl: 'div',
  className: null,
  margin: null,
  padding: {
    all: 'none'
  },
  status: 'open',
  type: 'permanent',
  position: 'left'
};
SidebarPage.displayName = 'Sidebar.Page';

function Sidebar({
  componentEl: Component,
  children,
  className,
  status,
  type,
  componentRef,
  top,
  bottom,
  position,
  border,
  width,
  zIndex,
  overlayProps,
  ...otherProps
}) {
  let cssClasses = ['st_sidebar', `st_sidebar--${position}`];
  let overlayCssClasses = ['st_sidebar__overlay'];
  const overlayStyles = [];
  cssClasses = addClassNames(className, cssClasses);
  cssClasses.push(`st_sidebar--${status}`);
  cssClasses.push(`st_sidebar--${type}`);
  if (border) cssClasses.push('st_sidebar--border');
  if (zIndex) {
    cssClasses = addClassNames(getZIndex(zIndex), cssClasses);
    overlayCssClasses = addClassNames(getZIndex(zIndex), overlayCssClasses);
  }
  const styles = otherProps.style || {};
  if (top !== null) {
    styles['--st-module-sidebar-top'] = top;
    overlayStyles['--st-module-sidebar-top'] = top;
  }
  if (bottom !== null) {
    styles['--st-module-sidebar-bottom'] = bottom;
    overlayStyles['--st-module-sidebar-bottom'] = bottom;
  }
  if (width !== null) {
    styles['--st-sidebar-width-open'] = width;
  }
  return /*#__PURE__*/React.createElement(React.Fragment, null, type === 'overlay' && ['opening', 'closing', 'open'].includes(status) && /*#__PURE__*/React.createElement("div", _extends({
    className: overlayCssClasses.join(' '),
    style: overlayStyles
  }, overlayProps)), /*#__PURE__*/React.createElement(Component, _extends({
    ref: componentRef,
    style: styles,
    className: cssClasses.join(' ')
  }, otherProps), children));
}
Sidebar.propTypes = {
  /** dom element or component to use for outermost wrapper */
  componentEl: props,
  /** ref for sidebar element */
  componentRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  /** extra CSS classes, if any */
  className: classNamePropTypes,
  /** whether sidebar is open (if it's being controlled) */
  status: PropTypes.oneOf(['open', 'closed', 'collapsed', 'opening', 'closing', 'collapsing']),
  /** type of sidebar */
  type: PropTypes.oneOf(['permanent', 'slide', 'push', 'overlay']),
  /** css override for top position of sidebar */
  top: PropTypes.string,
  /** css override for bottom position of sidebar */
  bottom: PropTypes.string,
  /** whether sidebar should have a border on the side */
  border: PropTypes.bool,
  /** the position of the sidebar */
  position: PropTypes.oneOf(['left', 'right']),
  /** override width of sidebar if needed. Will be set as the CSS value. Must include units */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * z-index for sidebar in overlay mode.
   * One of `minor`, `mid`, `mad`, `major`, `top` */
  zIndex: zIndexProps,
  /** props for overlay, e.g. onClick={closeSidebar} to close sidebar on click */
  overlayProps: PropTypes.any
};
Sidebar.defaultProps = {
  componentEl: 'div',
  componentRef: null,
  className: null,
  status: 'open',
  type: 'permanent',
  top: null,
  bottom: null,
  border: true,
  position: 'left',
  width: null,
  zIndex: null,
  overlayProps: {}
};
Sidebar.Section = SidebarSection;
Sidebar.Footer = SidebarFooter;
Sidebar.Header = SidebarHeader;
Sidebar.Toggle = SidebarToggle;
Sidebar.Page = SidebarPage;

export { Sidebar as S };
